import {
  DimensionType,
  Goal,
  MetricSlug,
  PeriodType,
} from '../../../../../../api/theHive/redeProto/types';
import {
  DIMENSION_TYPE_LABEL,
  METRIC_SLUG_LABEL,
  PERIOD_TYPE_LABEL,
  REGIONS,
  UF_SIGLA_BY_INT,
  regionLabelFromEnum,
  ufSiglaFromInt,
} from '../../../../../../constants/redeProto';
import { SectionCard } from '../../components/SectionCard';
import { StateFeedback } from '../../components/StateFeedback';
import { Table, TableWrap } from '../../components/StatesTable/styles';
import { EMPTY_VALUE, formatInt, truncate } from '../../utils/format';
import {
  AddButton,
  Field,
  FieldLabel,
  FormGrid,
  Input,
  RemoveButton,
  Select,
  Spacer,
} from './styles';
import useGoals from './useGoals';

const METRICS: MetricSlug[] = [
  'credenciados_ativos',
  'novos_credenciados',
  'captacao',
  'csat',
];
const DIMENSIONS: DimensionType[] = [
  'geral',
  'uf',
  'regiao',
  'polo',
  'tipologia',
];
const PERIODS: PeriodType[] = ['mensal', 'trimestral', 'anual'];
const UF_LIST = Object.values(UF_SIGLA_BY_INT).sort();

export function Goals(): JSX.Element {
  const {
    goals,
    poleOptions,
    typologyOptions,
    form,
    setForm,
    handleAdd,
    handleRemove,
    saving,
    loading,
    error,
  } = useGoals();

  const periodMax = form.periodType === 'mensal' ? 12 : 4;

  const refLabel = (goal: Goal): string => {
    switch (goal.dimensionType) {
      case 'polo':
        return (
          poleOptions.find((option) => option.id === goal.poloId)?.name ??
          String(goal.poloId ?? EMPTY_VALUE)
        );
      case 'tipologia':
        return (
          typologyOptions.find((option) => option.id === goal.typologyGroupId)
            ?.name ?? String(goal.typologyGroupId ?? EMPTY_VALUE)
        );
      case 'uf':
        return ufSiglaFromInt(goal.uf) || EMPTY_VALUE;
      case 'regiao':
        return regionLabelFromEnum(goal.regiao) ?? EMPTY_VALUE;
      default:
        return EMPTY_VALUE;
    }
  };

  const periodLabel = (goal: Goal): string =>
    goal.periodType === 'anual'
      ? PERIOD_TYPE_LABEL[goal.periodType]
      : `${PERIOD_TYPE_LABEL[goal.periodType]} · ${
          goal.periodNumber ?? EMPTY_VALUE
        }`;

  const renderRefField = (): JSX.Element | null => {
    if (form.dimensionType === 'geral') return null;

    const onChange = (value: string): void => setForm({ ...form, ref: value });

    if (form.dimensionType === 'regiao') {
      return (
        <Select value={form.ref} onChange={(e) => onChange(e.target.value)}>
          <option value="">Selecione...</option>
          {REGIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      );
    }
    if (form.dimensionType === 'uf') {
      return (
        <Select value={form.ref} onChange={(e) => onChange(e.target.value)}>
          <option value="">Selecione...</option>
          {UF_LIST.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      );
    }
    if (form.dimensionType === 'polo') {
      return (
        <Select value={form.ref} onChange={(e) => onChange(e.target.value)}>
          <option value="">Selecione...</option>
          {poleOptions.map((item) => (
            <option key={item.id} value={String(item.id)}>
              {truncate(item.name ?? item.city, 28)}
              {item.noticeName ? ` · ${truncate(item.noticeName, 18)}` : ''}
            </option>
          ))}
        </Select>
      );
    }
    // tipologia
    return (
      <Select value={form.ref} onChange={(e) => onChange(e.target.value)}>
        <option value="">Selecione...</option>
        {typologyOptions.map((item) => (
          <option key={item.id} value={String(item.id)}>
            {truncate(item.name, 28)}
            {item.noticeName ? ` · ${truncate(item.noticeName, 18)}` : ''}
          </option>
        ))}
      </Select>
    );
  };

  return (
    <StateFeedback loading={loading} error={error} empty={false}>
      <SectionCard
        title="Nova meta"
        subtitle="Meta geral da rede — persiste via POST /network/goals"
      >
        <FormGrid>
          <Field>
            <FieldLabel>Métrica</FieldLabel>
            <Select
              value={form.metricSlug}
              onChange={(e) =>
                setForm({ ...form, metricSlug: e.target.value as MetricSlug })
              }
            >
              {METRICS.map((item) => (
                <option key={item} value={item}>
                  {METRIC_SLUG_LABEL[item]}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <FieldLabel>Dimensão</FieldLabel>
            <Select
              value={form.dimensionType}
              onChange={(e) =>
                setForm({
                  ...form,
                  dimensionType: e.target.value as DimensionType,
                  ref: '',
                })
              }
            >
              {DIMENSIONS.map((item) => (
                <option key={item} value={item}>
                  {DIMENSION_TYPE_LABEL[item]}
                </option>
              ))}
            </Select>
          </Field>

          {form.dimensionType !== 'geral' && (
            <Field>
              <FieldLabel>Alvo</FieldLabel>
              {renderRefField()}
            </Field>
          )}

          <Field>
            <FieldLabel>Período</FieldLabel>
            <Select
              value={form.periodType}
              onChange={(e) =>
                setForm({
                  ...form,
                  periodType: e.target.value as PeriodType,
                  periodNumber: '',
                })
              }
            >
              {PERIODS.map((item) => (
                <option key={item} value={item}>
                  {PERIOD_TYPE_LABEL[item]}
                </option>
              ))}
            </Select>
          </Field>

          {form.periodType !== 'anual' && (
            <Field>
              <FieldLabel>
                {form.periodType === 'mensal' ? 'Mês' : 'Trimestre'}
              </FieldLabel>
              <Select
                value={form.periodNumber}
                onChange={(e) =>
                  setForm({ ...form, periodNumber: e.target.value })
                }
              >
                <option value="">Selecione...</option>
                {Array.from({ length: periodMax }, (_, i) => i + 1).map(
                  (item) => (
                    <option key={item} value={String(item)}>
                      {item}
                    </option>
                  )
                )}
              </Select>
            </Field>
          )}

          <Field>
            <FieldLabel>Ano</FieldLabel>
            <Input
              type="number"
              value={form.year}
              placeholder="2026"
              onChange={(e) => setForm({ ...form, year: e.target.value })}
            />
          </Field>

          <Field>
            <FieldLabel>Valor da meta</FieldLabel>
            <Input
              type="number"
              value={form.targetValue}
              placeholder="Ex.: 700"
              onChange={(e) =>
                setForm({ ...form, targetValue: e.target.value })
              }
            />
          </Field>

          <AddButton type="button" onClick={handleAdd} disabled={saving}>
            {saving ? 'Salvando...' : 'Adicionar'}
          </AddButton>
        </FormGrid>
      </SectionCard>

      <Spacer>
        <SectionCard
          title={`Metas configuradas (${goals.length})`}
          subtitle="Consolidado da rede por métrica, dimensão e período"
        >
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Métrica</th>
                  <th>Dimensão</th>
                  <th>Alvo</th>
                  <th>Período</th>
                  <th className="num">Ano</th>
                  <th className="num">Valor</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {goals.map((goal, index) => (
                  <tr
                    key={[
                      goal.metricSlug,
                      goal.dimensionType,
                      goal.poloId,
                      goal.typologyGroupId,
                      goal.uf,
                      goal.regiao,
                      goal.periodType,
                      goal.periodNumber,
                      goal.year,
                    ].join('-')}
                  >
                    <td>{METRIC_SLUG_LABEL[goal.metricSlug]}</td>
                    <td>{DIMENSION_TYPE_LABEL[goal.dimensionType]}</td>
                    <td>{refLabel(goal)}</td>
                    <td>{periodLabel(goal)}</td>
                    <td className="num">{goal.year}</td>
                    <td className="num">{formatInt(goal.targetValue)}</td>
                    <td className="num">
                      <RemoveButton
                        type="button"
                        onClick={() => handleRemove(index)}
                      >
                        Remover
                      </RemoveButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </SectionCard>
      </Spacer>
    </StateFeedback>
  );
}
